import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Typography,
  DialogActions,
  Alert,
  Box,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import CompaniesListWithPaymentSekelton from '@features/payments/components/PaymentCardDetail/CompaniesListWithPaymentSekelton';
import UploadedCompaniesRow from './UploadedCompaniesRow';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { CompanyData } from '../types';
import { useCreateMultipleCompanies } from '../hooks/useCreateMultipleCompanies';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';
import { authContext } from '@staffedupusa/shared-components';
import { useNavigate } from 'react-router-dom';
import { useUserCountry } from '@context/UserCountryContext';
import tzLookup from 'tz-lookup';

interface CsvUploadedCompaniesModalProps {
  open: boolean;
  selectedPlan: '1' | '2' | '4';
  handleClose: () => void;
  companiesData: CompanyData[];
  templateId: string;
  billingType: number;
}

const CsvUploadedCompaniesModal: React.FC<CsvUploadedCompaniesModalProps> = ({
  open,
  handleClose,
  selectedPlan,
  companiesData,
  templateId,
  billingType,
}) => {
  const { userCountry } = useUserCountry();
  const countryName =
    userCountry === 'au'
      ? 'Australia'
      : userCountry === 'ca'
        ? 'Canada'
        : 'United States';
  const { authData } = useContext(authContext);
  const navigate = useNavigate();
  const [modifiedCompaniesData, setModifiedComapiesData] =
    useState<CompanyData[]>(companiesData);
  const [fetchingCompaniesData, setFetchingCompaniesData] =
    useState<boolean>(true);
  const [showSuccessModal, setShowSuccess] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const { createCompanies, isCreatingCompanies, response } =
    useCreateMultipleCompanies({
      setErrorText,
    });
  const { paymentInfo } = useGetPaymantDetails();

  useEffect(() => {
    function fetchPlaceDetails() {
      setFetchingCompaniesData(true);
      const service = new (window as any).google.maps.places.PlacesService(
        document.createElement('div'),
      );
      const updatedData: CompanyData[] = [...companiesData];
      let completedRequests = 0;

      companiesData.forEach((item, index) => {
        const query = `${item.street_address}, ${item.city}, ${item.state}, ${item.zip_code}, ${countryName}`;
        const request = { query, fields: ['place_id', 'geometry'] };

        service.findPlaceFromQuery(request, (results: any, status: any) => {
          if (
            status ===
              (window as any).google.maps.places.PlacesServiceStatus.OK &&
            results.length > 0
          ) {
            const place = results[0];
            const tz = tzLookup(
              place.geometry.location.lat(),
              place.geometry.location.lng(),
            );
            updatedData[index] = {
              ...item,
              lat: place.geometry?.location?.lat() || '-',
              lng: place.geometry?.location?.lng() || '-',
              status: 'Valid',
            };

            service.getDetails(
              {
                placeId: place.place_id,
                fields: [
                  'website',
                  'formatted_phone_number',
                  'utc_offset_minutes',
                  'address_components',
                ],
              },
              (details: any, detailsStatus: string) => {
                if (
                  detailsStatus ===
                  (window as any).google.maps.places.PlacesServiceStatus.OK
                ) {
                  const countryComponent = details.address_components?.find(
                    (comp: any) => comp.types.includes('country'),
                  );
                  const countryCode = countryComponent?.short_name || '';

                  updatedData[index] = {
                    ...updatedData[index],
                    website: details.website || '',
                    phone: details.formatted_phone_number || '',
                    timezone: tz ? tz : '',
                    status:
                      countryCode.toLowerCase() === userCountry.toLowerCase()
                        ? 'Valid'
                        : 'Invalid Address',
                  };
                }
                completedRequests++;
                if (completedRequests === companiesData.length) {
                  setModifiedComapiesData(updatedData);
                  setFetchingCompaniesData(false);
                }
              },
            );
          } else {
            updatedData[index] = { ...item, status: 'Invalid Address' };
            completedRequests++;
            if (completedRequests === companiesData.length) {
              setModifiedComapiesData(updatedData);
              setFetchingCompaniesData(false);
            }
          }
        });
      });
    }

    if (open) fetchPlaceDetails();
  }, [open, companiesData?.length]);

  const handleSubmit = () => {
    // if (billingType !== 0 && !hasPaymentMethod) {
    //   toast.error(
    //     'Please Attach a Payment Method and  billing Cycle to upgrade ',
    //   );
    //   navigate('/enterprise/billing/#Payment-Info');
    //   return;
    // }
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          toast.success(data.message);
          setShowSuccess(true);
        }
      },
    };
    const payload = modifiedCompaniesData.map((item) => ({
      country: userCountry === 'au' ? 'AU' : userCountry === 'ca' ? 'CA' : 'US',
      email: item.email,
      user_name: item.user_name,
      business_name: item.company_name,
      name: item.company_name,
      s_address1: item.street_address,
      s_city: item.city,
      s_state: item.state,
      s_zip: item.zip_code,
      postal_code: item.zip_code,
      website: item.website,
      phone: item.phone,
      timezone: item.timezone,
      s_lat: item.lat,
      s_lon: item.lng,
      status: item.status,
      physical_address: true,
    }));

    createCompanies(
      {
        companies: payload,
        etemplate_id: templateId,
        ...(billingType !== 0 ? { plan_id: selectedPlan } : {}),
      },
      options,
    );
  };

  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo?.object &&
      paymentInfo?.object?.data?.length > 0 &&
      (authData?.user?.billing_cycle ?? 0 > 0)
    ) {
      setHasPaymentMethod(true);
    }
  }, [paymentInfo]);

  const renderSuccess = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        sx={{ margin: '5px 25px 25px 25px' }}
        gap={3}
        justifyContent="center"
        alignItems="center"
      >
        {Boolean(response?.success?.length) && (
          <Box mt={2} width="100%">
            <Alert severity="success">
              <Typography variant="h6">
                Accounts created and email invites have been sent to the
                following emails:
              </Typography>
              <ul>
                {response?.success.map((email: string) => (
                  <Typography component="li" key={email}>
                    {email}
                  </Typography>
                ))}
              </ul>
            </Alert>
          </Box>
        )}
        {Boolean(response?.error?.length) && (
          <Box mt={2} width="100%">
            <Alert severity="error">
              <Typography variant="h6">
                Following emails already exist
              </Typography>
              <ul>
                {response?.error.map((email: string) => (
                  <Typography component="li" key={email}>
                    {email}
                  </Typography>
                ))}
              </ul>
            </Alert>
          </Box>
        )}
        <Button
          color="success"
          variant="contained"
          startIcon={<Check />}
          onClick={handleClose}
          sx={{ width: '250px', textAlign: 'center', margin: '0px auto' }}
        >
          Got it
        </Button>
      </Box>
    );
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={showSuccessModal ? 'sm' : 'lg'}
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <Typography variant="h4">
          {showSuccessModal
            ? 'Your companies were successfully assigned to your template'
            : 'Companies Detail'}
        </Typography>
      </DialogTitle>
      {showSuccessModal ? (
        renderSuccess()
      ) : (
        <>
          <DialogContent>
            {errorText && (
              <Alert sx={{ margin: 2 }} severity="error">
                {errorText}
              </Alert>
            )}
            {fetchingCompaniesData ? (
              <CompaniesListWithPaymentSekelton />
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>Sr.</b>
                      </TableCell>
                      <TableCell>
                        <b>Email</b>
                      </TableCell>
                      <TableCell>
                        <b>Name</b>
                      </TableCell>
                      <TableCell>
                        <b>Company</b>
                      </TableCell>
                      <TableCell>
                        <b>Address</b>
                      </TableCell>
                      <TableCell>
                        <b>City</b>
                      </TableCell>
                      <TableCell>
                        <b>State</b>
                      </TableCell>
                      <TableCell>
                        <b>Zip Code</b>
                      </TableCell>
                      <TableCell>
                        <b>Action</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modifiedCompaniesData.map((row, index) => (
                      <UploadedCompaniesRow
                        item={row}
                        index={index}
                        setModifiedComapiesData={setModifiedComapiesData}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions
            sx={{ justifyContent: 'center', marginBottom: '20px' }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={fetchingCompaniesData}
              color="secondary"
              sx={{ width: '200px', fontSize: '1rem' }}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={isCreatingCompanies}
              disabled={
                fetchingCompaniesData ||
                modifiedCompaniesData?.some(
                  (item) => item.status === 'Invalid Address',
                )
              }
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              sx={{
                width: '200px',
                fontSize: '1rem',
                fontWeight: 'bold',
              }}
            >
              Confirm Import
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CsvUploadedCompaniesModal;
